/**
 * Gemeinsamer Zwischenspeicher der Gastbilder. Jede Bildadresse wird einmal geladen und als
 * Object-URL gehalten, solange die Seite lebt: Kopfbild, Vorschaubild, Vollbild und Zurückblättern
 * nehmen dasselbe Bild. Vorher lud jede Anzeige neu — am Handy wartete man bei jedem Wischen aufs
 * Netz. Über der Grenze fallen die am längsten nicht gezeigten Bilder heraus und geben ihren
 * Speicher frei.
 */
const LIMIT = 80;
// Ein Abruf, der nie endet, hielte die Adresse sonst bis zum Neuladen der Seite fest.
const TIMEOUT_MS = 20_000;
// Eine eben verdrängte Adresse kann im selben Takt noch in ein `<img>` gewandert sein; erst etwas
// später freigeben, dann hat der Browser sie längst gelesen.
const REVOKE_DELAY_MS = 10_000;

interface Entry {
  promise: Promise<string>;
  objectUrl: string | null;
}

const cache = new Map<string, Entry>();

export function loadGuestImage(url: string, token: string): Promise<string> {
  const hit = cache.get(url);
  if (hit) {
    touch(url, hit);
    return hit.promise;
  }
  const entry: Entry = { promise: Promise.resolve(''), objectUrl: null };
  entry.promise = fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal: timeoutSignal(),
  })
    .then((response) =>
      response.ok ? response.blob() : Promise.reject(new Error(String(response.status))),
    )
    .then((blob) => {
      entry.objectUrl = URL.createObjectURL(blob);
      evictOverLimit();
      return entry.objectUrl;
    })
    .catch((error: unknown) => {
      // Ein Fehlschlag bleibt nicht liegen: die nächste Anzeige versucht es neu. Nur der eigene
      // Eintrag geht — ein neuerer unter derselben Adresse bleibt.
      if (cache.get(url) === entry) cache.delete(url);
      throw error;
    });
  cache.set(url, entry);
  evictOverLimit();
  return entry.promise;
}

/** Das Bild, falls es schon geladen ist — ohne Warten und ohne Netz. Zählt als gezeigt. */
export function peekGuestImage(url: string): string | null {
  const hit = cache.get(url);
  if (!hit?.objectUrl) return null;
  touch(url, hit);
  return hit.objectUrl;
}

/**
 * Vergisst ein geladenes Bild, dessen Adresse nicht mehr taugt (`onError` am `<img>`): die
 * nächste Anzeige lädt neu, meist aus dem Browser-Cache.
 */
export function forgetGuestImage(url: string) {
  const entry = cache.get(url);
  if (!entry?.objectUrl) return;
  cache.delete(url);
  revokeLater(entry.objectUrl);
}

/**
 * Lädt ein Bild vorab, etwa das nächste beim Blättern. Ein Fehler hier bleibt still: gezeigt wird
 * das Bild erst über `loadGuestImage`, und dort sieht der Gast den Fehler.
 */
export function preloadGuestImage(url: string | null | undefined, token: string | undefined) {
  if (!url || !token) return;
  loadGuestImage(url, token).catch(() => undefined);
}

function touch(url: string, entry: Entry) {
  cache.delete(url);
  cache.set(url, entry);
}

function evictOverLimit() {
  for (const [url, entry] of cache) {
    if (cache.size <= LIMIT) return;
    // Laufende Abrufe bleiben: ihre Adresse entsteht erst noch und würde sonst nie freigegeben.
    if (!entry.objectUrl) continue;
    cache.delete(url);
    revokeLater(entry.objectUrl);
  }
}

function revokeLater(objectUrl: string) {
  setTimeout(() => URL.revokeObjectURL(objectUrl), REVOKE_DELAY_MS);
}

function timeoutSignal(): AbortSignal | undefined {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(TIMEOUT_MS)
    : undefined;
}

/** Nur für Tests: jeder Test beginnt mit leerem Speicher. */
export function clearGuestImageCache() {
  for (const entry of cache.values()) {
    if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
  }
  cache.clear();
}
